/**
 * QuotesSideRail — the collapsible left panel shared by every page in the
 * Quotes area (quotes list, Intake Requests, intake detail, …), so the
 * navigation stays identical wherever you are under /quotes.
 *
 * Self-sufficient: fetches its own create-policy, pending-approvals count and
 * intake count. The quotes list passes live `counts` + an `onSelectFilter`
 * override; other pages omit them and a filter click simply navigates to
 * /quotes?filter=<key>.
 */
import { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Divider, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add, AllInclusive, Drafts, HourglassTop, CheckCircleOutline, Send, ThumbDown,
  Schedule, ChevronLeft, ChevronRight, ErrorOutline, ChatBubbleOutline,
  AssignmentTurnedIn, Inbox,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { getQuoteCreatePolicy, getPendingApprovals } from '../../services/quoteService';
import { getIntakeCounts } from '../../services/intakeService';
import { usePermissions } from '../../hooks/usePermissions';
import { roleAtLeast } from '../../constants/roles';
import type { QuoteStatus } from '../../types/quote';

export type QuoteFilter = 'all' | 'mine' | QuoteStatus;

export const QUOTE_FILTERS: { key: QuoteFilter; label: string; icon: React.ReactNode; color: string; dividerBefore?: boolean }[] = [
  { key: 'all', label: 'All Quotes', icon: <AllInclusive />, color: '#555' },
  { key: 'mine', label: 'My Quotes', icon: <Drafts />, color: '#1565c0' },
  { key: 'Draft', label: 'Drafts', icon: <Drafts />, color: '#9e9e9e' },
  { key: 'Internal Review', label: 'Awaiting Approval', icon: <HourglassTop />, color: '#e65100' },
  { key: 'Approved Internal', label: 'Approved', icon: <CheckCircleOutline />, color: '#2e7d32' },
  { key: 'Rejected', label: 'Rejected', icon: <ErrorOutline />, color: '#d32f2f' },
  { key: 'Sent', label: 'Sent to Client', icon: <Send />, color: '#1976d2', dividerBefore: true },
  { key: 'Changes Requested', label: 'Changes Requested', icon: <ChatBubbleOutline />, color: '#e65100' },
  { key: 'Accepted', label: 'Client Accepted', icon: <CheckCircleOutline />, color: '#00695c' },
  { key: 'Declined', label: 'Client Declined', icon: <ThumbDown />, color: '#c62828' },
  { key: 'Expired', label: 'Expired', icon: <Schedule />, color: '#616161' },
];

const COLLAPSE_KEY = 'belab.quotes.rail.collapsed';

interface QuotesSideRailProps {
  /** Which entry highlights: a quote filter key, 'intake', or 'pending-approvals'. */
  active: QuoteFilter | 'intake' | 'pending-approvals' | 'none';
  /** Live per-filter counts (quotes list only — omitted elsewhere, chips hide). */
  counts?: Record<string, number>;
  /** Override for filter clicks (quotes list). Default: navigate to /quotes?filter=key. */
  onSelectFilter?: (key: QuoteFilter) => void;
  /** Overrides for the create buttons. Defaults navigate to /quotes with a query flag. */
  onNewQuote?: () => void;
  onNewInternalQuote?: () => void;
  /** Refresh signal for the intake badge (bump after converting/cancelling). */
  intakeCountKey?: number;
}

export const QuotesSideRail = ({
  active, counts, onSelectFilter, onNewQuote, onNewInternalQuote, intakeCountKey,
}: QuotesSideRailProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = usePermissions();
  // RBAC Phase B (2026-08-06): quote approval is admin-only.
  const canApprove = roleAtLeast(role, 'admin');
  const [createMinRole, setCreateMinRole] = useState<string>('admin');
  const canCreate = roleAtLeast(role, createMinRole);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [intakeCount, setIntakeCount] = useState(0);
  const [open, setOpen] = useState(() => localStorage.getItem(COLLAPSE_KEY) !== '1');

  useEffect(() => {
    getQuoteCreatePolicy().then((p) => setCreateMinRole(p.minRole)).catch(() => { /* keep default */ });
  }, []);
  useEffect(() => {
    if (!canApprove) { setPendingApprovalCount(0); return; }
    getPendingApprovals()
      .then((items) => setPendingApprovalCount(items.length))
      .catch(() => setPendingApprovalCount(0));
  }, [canApprove]);
  useEffect(() => {
    getIntakeCounts().then((c) => setIntakeCount(c.submitted)).catch(() => setIntakeCount(0));
  }, [intakeCountKey]);

  const toggleOpen = () => {
    setOpen((o) => {
      localStorage.setItem(COLLAPSE_KEY, o ? '1' : '0');
      return !o;
    });
  };

  const selectFilter = (key: QuoteFilter) => {
    if (onSelectFilter) onSelectFilter(key);
    else navigate(`${ROUTES.QUOTES}?filter=${encodeURIComponent(key)}`);
  };

  return (
    <Box sx={{
      width: open ? 220 : 44, borderRight: `1px solid ${theme.palette.divider}`,
      display: 'flex', flexShrink: 0, transition: 'width 0.25s ease', overflow: 'hidden',
      position: 'relative',
    }}>
      <Box sx={{ width: 220, display: 'flex', flexDirection: 'column', minWidth: 220 }}>
        <Box sx={{ p: 2, pb: 1, opacity: open ? 1 : 0, transition: 'opacity 0.2s ease' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Quotes</Typography>
          {canCreate && (
            <>
              <Button variant="contained" size="small" startIcon={<Add />} fullWidth
                onClick={onNewQuote ?? (() => navigate(`${ROUTES.QUOTES}?new=1`))}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', mb: 1 }}>
                New Quote
              </Button>
              <Button variant="outlined" size="small" startIcon={<Add />} fullWidth
                onClick={onNewInternalQuote ?? (() => navigate(`${ROUTES.QUOTES}?newInternal=1`))}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', mb: 1.5 }}>
                New Internal Quote
              </Button>
            </>
          )}
        </Box>
        <List dense sx={{ px: 1, flex: 1, overflow: 'auto', opacity: open ? 1 : 0, transition: 'opacity 0.2s ease' }}>
          {QUOTE_FILTERS.map((f) => (
            <Box key={f.key}>
              {f.dividerBefore && (
                <Box sx={{ px: 0.5, py: 0.75 }}>
                  <Divider />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, px: 0.5, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Client
                  </Typography>
                </Box>
              )}
              <ListItemButton selected={active === f.key}
                onClick={() => selectFilter(f.key)}
                sx={{
                  borderRadius: '8px', mb: 0.25, py: 0.5,
                  '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: f.color }}>
                  {f.icon}
                </ListItemIcon>
                <ListItemText primary={f.label} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                {counts && (counts[f.key] || 0) > 0 && (
                  <Chip label={counts[f.key]} size="small"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600,
                      bgcolor: active === f.key ? alpha(f.color, 0.15) : alpha('#000', 0.06) }} />
                )}
              </ListItemButton>
            </Box>
          ))}
          {/* Intake Requests — client intake inbox (tokenized-link submissions) */}
          <Box>
            <Box sx={{ px: 0.5, py: 0.75 }}><Divider /></Box>
            <ListItemButton
              selected={active === 'intake'}
              onClick={() => navigate(ROUTES.INTAKE_REQUESTS)}
              sx={{
                borderRadius: '8px', mb: 0.25, py: 0.5,
                '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}>
              <ListItemIcon sx={{ minWidth: 32, color: '#1565c0' }}>
                <Inbox />
              </ListItemIcon>
              <ListItemText primary="Intake Requests" primaryTypographyProps={{ fontSize: '0.8rem' }} />
              {intakeCount > 0 && (
                <Chip label={intakeCount} size="small"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, color: '#1565c0',
                    bgcolor: alpha('#1565c0', 0.15) }} />
              )}
            </ListItemButton>
          </Box>
          {/* Pending Approvals — reviewer+ only; links to the approvals queue */}
          {canApprove && (
            <Box>
              <ListItemButton
                selected={active === 'pending-approvals'}
                onClick={() => navigate(ROUTES.PENDING_APPROVALS)}
                sx={{
                  borderRadius: '8px', mb: 0.25, py: 0.5,
                  '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: '#e65100' }}>
                  <AssignmentTurnedIn />
                </ListItemIcon>
                <ListItemText primary="Pending Approvals" primaryTypographyProps={{ fontSize: '0.8rem' }} />
                {pendingApprovalCount > 0 && (
                  <Chip label={pendingApprovalCount} size="small"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, color: '#e65100',
                      bgcolor: alpha('#e65100', 0.15) }} />
                )}
              </ListItemButton>
            </Box>
          )}
        </List>
      </Box>
      <Box sx={{ position: 'absolute', left: open ? 196 : 8, top: 16, zIndex: 10, transition: 'left 0.25s ease' }}>
        <IconButton size="small" onClick={toggleOpen}
          sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, width: 24, height: 24,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
          {open ? <ChevronLeft sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
    </Box>
  );
};
